import styled from '@emotion/styled';
import React from 'react';
import {ECancelSubmitButtonRow, EForm} from 'webapp-lib/Components';
import {generateRandomUnsafeId} from 'webapp-lib/utils';

import {CostCenterAutocomplete} from '#/Autocomplete/CostCenterAutocomplete';
import {Dialog} from '#/Dialog/Dialog';
import {useMasterdata} from '_/hooks/useMasterdata';
import {toInternalCostCenterId} from '_/utilities/costCenter';
import {
  EFAutocomplete,
  EFTextArea,
  EFTextInput,
  requiredAndNonWhitespaceRule,
  requiredRule,
  useForm,
  useWatch,
} from '_/utilities/form';

import {useEventTagColors} from '../../hooks/useEventTagColors';
import {DraggableWorkLogSuggestion} from '../../types';
import {toMinutes, toSeconds} from '../../utilities/date';
import {createCostCenterWithDisplayName} from '../../utilities/workLog';

enum CreateFavoriteFormFields {
  CostCenterId = 'costCenterId',
  Title = 'title',
  Description = 'description',
  DefaultDuration = 'defaultDuration',
  Color = 'color',
}

type CreateFavoriteFormInputs = {
  [CreateFavoriteFormFields.CostCenterId]: string;
  [CreateFavoriteFormFields.Title]: string;
  [CreateFavoriteFormFields.Description]?: string;
  [CreateFavoriteFormFields.DefaultDuration]?: number | string;
  [CreateFavoriteFormFields.Color]: string;
};

export const CreateFavoriteDialog = ({
  data,
  isOpen,
  onClose,
  onEdit,
}: {
  data?: DraggableWorkLogSuggestion;
  isOpen: boolean;
  onEdit: (favorite: DraggableWorkLogSuggestion) => void;
  onClose: () => void;
}): React.ReactElement => {
  const masterdata = useMasterdata();
  const {getRandomColor, colorAutocompleteOptions} = useEventTagColors();
  const activityData = data?.activity;

  const inputCostCenterId =
    activityData?.casusId ??
    toInternalCostCenterId(activityData?.internalHoursCategoryId);

  const {control, formState, handleSubmit} = useForm<CreateFavoriteFormInputs>({
    defaultValues: {
      costCenterId: inputCostCenterId || '',
      title: data?.title || '',
      description: data?.description || '',
      defaultDuration: data?.defaultDurationSeconds
        ? toMinutes(data.defaultDurationSeconds)
        : '',
      color: data?.color || getRandomColor(),
    },
    onSubmit: async ({
      costCenterId,
      color,
      title,
      description,
      defaultDuration,
    }) => {
      const result: DraggableWorkLogSuggestion = {
        id: data?.id ?? generateRandomUnsafeId(),
        defaultDurationSeconds: defaultDuration
          ? toSeconds(Number(defaultDuration))
          : undefined,
        color,
        title,
        description,
        activity: await createCostCenterWithDisplayName(
          costCenterId,
          masterdata
        ),
      };

      onEdit(result);
      onClose();
    },
  });

  const watchedColor = useWatch({
    control,
    name: CreateFavoriteFormFields.Color,
  });

  return (
    <Dialog headerText='Favorit' isShown={isOpen} size='tiny' onClose={onClose}>
      <EForm onSubmit={handleSubmit}>
        <EFTextArea
          control={control}
          label='Titel'
          minRows={2}
          name={CreateFavoriteFormFields.Title}
          rules={requiredAndNonWhitespaceRule}
          autoFocus
        />
        <CostCenterAutocomplete
          control={control}
          internalHoursCategories={masterdata.internalHoursCategories ?? []}
          label='Dossier'
          name={CreateFavoriteFormFields.CostCenterId}
          rules={requiredRule}
        />
        <EFTextArea
          control={control}
          label='Beschreibung'
          minRows={4}
          name={CreateFavoriteFormFields.Description}
        />
        <EFTextInput
          control={control}
          label='Standard Dauer (Minuten)'
          min={0}
          name={CreateFavoriteFormFields.DefaultDuration}
          placeholder='Minuten'
          type='number'
        />
        <ColorPickerBox color={watchedColor}>
          <EFAutocomplete
            control={control}
            label='Farbe'
            name={CreateFavoriteFormFields.Color}
            options={colorAutocompleteOptions}
          />
        </ColorPickerBox>
        <ECancelSubmitButtonRow
          isSubmitting={formState.isSubmitting}
          onCancel={onClose}
        />
      </EForm>
    </Dialog>
  );
};

export const ColorPickerBox = styled.div<{color: string}>`
  .MuiTextField-root {
    padding-left: var(--gutter-little);
    border-left: ${({color}): string => color} solid 12px;
  }
`;
